import { Component, Input, ChangeDetectionStrategy } from "@angular/core";
import { Type } from "../../../../shared/components";
import { Customer } from "../../../../shared/types/customers";
import { getApp } from "../app";
@Component({
    selector: "app-filter-fields",
    templateUrl: "./filter-fields.component.html",
    styleUrls: ["./filter-fields.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FilterFieldsComponent {
  fieldList: string[] = [];
  optionSearchTerm = "";
  @Input() types: Type[] = [];

  app = getApp((app) => {
    this.app = app;
  });

  constructor() {}

  getFieldOptions() {
    return new Map<string, Set<any>>();
  }

  getFeatureFields() {
    const types = new Set(this.types);
    if (!types.has("thread")) {
      return [];
    }
    return [
      this.app.fieldId.thread.changeClasses,
      this.app.fieldId.thread.omfStatus,
      this.app.fieldId.post.resolveClass
    ];
  }

  getFilterFields() {
    return (this.app.state.filterByPage?.length ?? 0) > 0;
  }

  // The date-range group (specific/from/to keys) a field belongs to, or null if
  // it isn't part of a date-range filter. Built on getRangeFields so the from/to
  // mapping stays defined in one place.
  private getRangeGroup(field: string | null) {
    const groups = [
      this.app.datesFilter.getRangeFields(this.app.fieldId.thread.update_time),
      this.app.datesFilter.getRangeFields(this.app.fieldId.thread.validationDate)
    ];
    return (
      groups.find(
        (g) => field === g.specific || field === g.from || field === g.to
      ) ?? null
    );
  }

  getDisplayValue(field: string, value: any): string {
    if (field === null) {
      return "" + value;
    }

    // A date-range field's "specific" value is a dateInterval type that
    // getFieldValueAsText can't format; render it via its timestamp from-helper.
    const range = this.getRangeGroup(field);
    if (range && field === range.specific) {
      return this.app.field.getFieldValueAsText(range.from, value);
    }

    if (value instanceof Set) {
      const arr = Array.from(value);
      if (arr.length === 1) {
        value = arr[0];
      } else {
        return arr.map((v) => this.getDisplayValue(field, v)).join(", ");
      }
    }

    if (
      this.app.field.isMultiple(field) &&
      this.app.field.getType(field) === "user-options"
    ) {
      const id = Array.isArray(value) ? value[0] : value;
      return this.app.users.getOptionName(id);
    }
    //set multiple to false to get single value as text only for filter display
    if (this.app.field.isMultiple(field)) {
      this.app.field.getFieldSettings(field).multiple = false;
    }
    return this.app.field.getFieldValueAsText(field, value);
  }

  getFilteredCurrentOptions(): any[] {
    const field = this.app.filterList.field;
    const term = this.optionSearchTerm.toLowerCase();
    const type = this.getFieldType(field);

    switch (type) {
      case "user-options":
        return this.app.field
          .getUserOptions(field!)
          .filter((u: any) => !term || u.name?.toLowerCase().includes(term));
      case "dataList":
        return this.app.field
          .getDatalistOptions(field!)
          .filter((opt: string) => !term || opt.toLowerCase().includes(term));
      default:
        return this.app.filterTable
          .getOptions(field!)
          .filter(
            (opt: string) =>
              !term ||
              this.app.filterTree
                .getOptionText(field!, opt)
                .toLowerCase()
                .includes(term)
          );
    }
  }

  getFieldType(field: string | null) {
    if (field === null) {
      return "";
    }
    return this.app.field.getType(field);
  }

  getFilterChipText(filter: any): string {
    // For a date range, the chip renders on the "from" entry and folds in the
    // matching "to" entry (the "to" chip itself is hidden in the template).
    const range = this.getRangeGroup(filter.filterLabelField);
    if (range && filter.filterLabelField === range.from) {
      const toFilter = this.app.state.filterByPage.find(
        (f) => f.filterLabelField === range.to
      );
      const fromStr = this.getDisplayValue(range.from, filter.values);
      const toStr = toFilter
        ? this.getDisplayValue(range.to, toFilter.values)
        : "";
      return `${this.app.field.getLabel(range.specific)} :  ${fromStr} - ${toStr}`;
    }
    return `${this.app.field.getLabel(filter.filterLabelField)} :  ${this.getDisplayValue(filter.filterLabelField, filter.values).toUpperCase()}`;
  }

  openDatesFilter(event: MouseEvent) {
    event.stopPropagation();
    this.app.datesFilter.showComponent = !this.app.datesFilter.showComponent;
  }

  getDateFilterLabel(): string {
    const range = this.getRangeGroup(this.app.filterList.field);
    if (!range) {
      return "";
    }
    const from = this.app.state.filterByPage.find(
      (f) => f.filterLabelField === range.from
    );
    const to = this.app.state.filterByPage.find(
      (f) => f.filterLabelField === range.to
    );
    if (from || to) {
      const fromStr = from
        ? this.app.field.getFieldValueAsText(range.from, from.values)
        : "";
      const toStr = to
        ? this.app.field.getFieldValueAsText(range.to, to.values)
        : "";
      return `${fromStr} - ${toStr}`;
    }
    return "";
  }

  getActiveFilters() {
    if (this.app.type === "thread") {
      return this.app.state.filterByPage;
    }
    return this.app.filterHeaderTable.filters;
  }

  async selectOption(field: string, value: string) {
    if (this.app.type === "thread") {
      const index = this.app.state.filterByPage.findIndex(
        (f) => f.filterLabelField === field && f.values === value
      );
      if (index !== -1) {
        this.app.state.filterByPage.splice(index, 1);
      } else {
        this.app.state.filterByPage.push({
          values: value,
          filterView: this.app.state.view,
          filterLabelField: field
        });
      }
      this.app.state.next();
      this.app.spinner.showSpinner();
      await this.app.thread.getThreadSearchResult();
      this.app.spinner.hideSpinner();
      this.app.paginator.resetPagination();
    } else {
      this.app.filterHeaderTable.filter(field, value);
    }
  }

  async clearFilter(fieldId: string) {
    if (this.app.type !== "thread") {
      await this.app.filterHeaderTable.clearFilter(fieldId);
    }
    this.app.model[fieldId] = "";

    const range = this.getRangeGroup(fieldId);
    if (range) {
      this.app.state.filterByPage = this.app.state.filterByPage.filter(
        (f) =>
          f.filterLabelField !== range.specific &&
          f.filterLabelField !== range.from &&
          f.filterLabelField !== range.to
      );
      this.app.datesFilter.clear();
    } else {
      const index = this.app.state.filterByPage.findIndex(
        (filterItem) => filterItem.filterLabelField === fieldId
      );
      if (index !== -1) {
        this.app.state.filterByPage.splice(index, 1);
      }
    }

    if (this.app.state.filterByPage.length === 0) {
      this.app.state.next({
        filterByPage: [],
        filters: {},
        filter: { values: new Set() }
      });
    } else {
      this.app.state.next();
    }

    if (this.app.filterFields.isQuickFilterField(fieldId)) {
      this.app.filterFields.quickFilterSelected = "";
    } else if (
      fieldId === "thread.dinCodeRespName" &&
      this.app.customers.expectCurrent === Customer.DB &&
      this.app.filterFields.quickFilterSelected === "own"
    ) {
      // DB's "My cases" quick filter is backed by the dinCodeRespName (Employee OM)
      // filter; removing it should reset the quick filter to "All cases".
      this.app.filterFields.quickFilterSelected = "";
    }

    this.app.spinner.showSpinner();
    await this.app.thread.getThreadSearchResult();
    this.app.spinner.hideSpinner();

    if (range) {
      this.app.filterList.field = range.specific;
      this.app.filterList.open = true;
    } else if (!this.app.filterFields.isQuickFilterField(fieldId)) {
      this.app.filterList.field = fieldId;
      this.app.filterList.open = true;
      this.optionSearchTerm = "";
    }
  }
}
